import { QuestionIcon } from "@phosphor-icons/react/dist/csr/Question";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Stack } from "@volca/ui/components/ui/stack";
import { WorkspaceMenu } from "@/app-shell/workspace-menu";
import { useGetBusiness } from "@/business/query";
import { useAuth } from "@/core/auth";
import { useBusiness } from "@/core/business";
import { openSupportChat, useHasPylonAccess } from "@/core/pylon";
import { usePylonChatAnchor } from "@/core/pylon/use-pylon-chat-anchor";
import {
  PAY_BREAKDOWN_PATH,
  isPayBreakdownPath,
} from "@/payroll/pay-breakdown/routes";
import {
  isPayrollEditorPath,
  PAYROLL_EDITOR_PATH,
} from "@/payroll/settings/routes";
import Profile from "@/profile";
import { useGetMyUser } from "@/user/query";
import { isTech } from "@/user/utils";

export const WorkspaceHeader = ({
  onImpersonate,
}: {
  onImpersonate?: () => void;
}) => {
  const { data: user } = useGetMyUser();
  const { user: authUser } = useAuth();
  const { businessId } = useBusiness();
  const { data: business } = useGetBusiness(businessId);
  const { pathname } = useLocation();
  const technician = isTech(user?.role ?? authUser?.role);
  const [profileOpen, setProfileOpen] = useState(false);
  const hasSupport = useHasPylonAccess();
  const supportRef = usePylonChatAnchor(hasSupport, "bottom", false);
  const showPayrollRules =
    technician &&
    business?.can_edit_payroll_rules === true &&
    !isPayrollEditorPath(pathname);
  const showMyPay = technician && !isPayBreakdownPath(pathname);
  const showHelp = !technician && hasSupport;

  return (
    <>
      <Stack
        render={<header aria-label="Workspace" />}
        direction="row"
        justify="between"
        align="center"
        gap={4}
        className="h-12 shrink-0 border-b bg-surface-raised px-4"
      >
        <WorkspaceMenu
          presentation="header"
          onImpersonate={onImpersonate}
          onProfile={() => setProfileOpen(true)}
        />
        <Stack direction="row" align="center" gap={2}>
          {showPayrollRules && (
            <Button
              variant="ghost"
              size="sm"
              nativeButton={false}
              render={<Link to={PAYROLL_EDITOR_PATH} />}
            >
              Pay rules
            </Button>
          )}
          {showMyPay && (
            <Button
              variant="ghost"
              size="sm"
              nativeButton={false}
              render={<Link to={PAY_BREAKDOWN_PATH} />}
            >
              My pay
            </Button>
          )}
          {showHelp && (
            <Button
              ref={supportRef}
              variant="ghost"
              size="sm"
              onClick={openSupportChat}
              aria-label="Help: contact support"
            >
              <QuestionIcon />
              Help
            </Button>
          )}
        </Stack>
      </Stack>
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="max-h-[calc(100svh-4rem)] overflow-y-auto sm:max-w-2xl">
          <DialogHeader className="sr-only">
            <DialogTitle>Profile</DialogTitle>
            <DialogDescription>Manage your account profile.</DialogDescription>
          </DialogHeader>
          {profileOpen && <Profile />}
        </DialogContent>
      </Dialog>
    </>
  );
};
